import type { CSSProperties } from "react";

function encodeEntities(s: string): string {
  return Array.from(s)
    .map((c) => `&#${c.codePointAt(0)};`)
    .join("");
}

type Props = {
  email: string;
  className?: string;
  style?: CSSProperties;
  /**
   * Visible label override. Defaults to the email itself (also encoded).
   * Pass `null` to render a link with no visible text (e.g. when the
   * label is provided by a sibling element).
   */
  label?: string | null;
};

/**
 * Crée un lien mailto dont le href et le texte visible sont encodés en
 * entités HTML décimales. Les scrapers naïfs qui regex `[a-z0-9]+@` sur le
 * HTML brut ne trouvent rien, le navigateur décode au parsing.
 */
export function ObfuscatedEmailLink({ email, className, style, label }: Props) {
  const encodedHref = encodeEntities(email);
  const visible = label === undefined ? encodedHref : label === null ? "" : encodeEntities(label);

  const attrs: string[] = [`href="mailto:${encodedHref}"`];
  if (className) {
    attrs.push(`class="${escapeAttribute(className)}"`);
  }
  if (style) {
    const inline = Object.entries(style)
      .map(([k, v]) => `${camelToKebab(k)}:${String(v)}`)
      .join(";");
    if (inline) attrs.push(`style="${escapeAttribute(inline)}"`);
  }

  return (
    <span
      // biome-ignore lint/security/noDangerouslySetInnerHtml: encodage entités HTML anti-bots, contenu construit ici
      dangerouslySetInnerHTML={{
        __html: `<a ${attrs.join(" ")}>${visible}</a>`,
      }}
    />
  );
}

function escapeAttribute(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function camelToKebab(s: string): string {
  return s.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);
}
